import React, { FormEvent, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate } from '@tanstack/react-location';
import { useAuth } from '../hooks/useAuth';
import { changePasswordRequest } from '../requests/session.request';
import { withAuthenticationRequired } from '../providers/withAuthenticationRequired';

const PASSWORD_RULES =
  'At least 10 characters, with an uppercase letter, a lowercase letter and a digit.';

function ChangePasswordPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setErrors(['The new passwords do not match.']);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    try {
      await changePasswordRequest(currentPassword, newPassword);
      navigate({ to: '/home', replace: true });
    } catch (e) {
      const data = e?.response?.data;
      setErrors([data?.message ?? 'Password change failed.', ...(data?.details ?? [])]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container style={{ maxWidth: 560 }} className="mt-5">
      <h3>Change password</h3>
      {user?.mustChangePassword && (
        <Alert variant="info">
          You signed in with a temporary password. Choose a new one to continue.
        </Alert>
      )}
      <Form onSubmit={onSubmit}>
        {errors.length > 0 && (
          <Alert variant="danger">
            {errors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </Alert>
        )}
        <Form.Group className="mb-3" controlId="currentPassword">
          <Form.Label>Current password</Form.Label>
          <Form.Control
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="newPassword">
          <Form.Label>New password</Form.Label>
          <Form.Control
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <Form.Text muted>{PASSWORD_RULES}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3" controlId="confirmPassword">
          <Form.Label>Confirm new password</Form.Label>
          <Form.Control
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </Form.Group>
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? <Spinner animation="border" size="sm" /> : 'Change password'}
        </Button>
        <Button variant="link" className="ms-2" onClick={() => void logout()}>
          Log out
        </Button>
      </Form>
    </Container>
  );
}

export default withAuthenticationRequired(ChangePasswordPage);
